import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, CircularProgress, Menu, MenuItem, Button, Tabs, Tab,
    Divider, Grid, Typography, Stack,
    ToggleButton, ToggleButtonGroup
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PreInterviewCard from '../components/PreInterviewCard';
import PreInterviewViewModal from '../components/PreInterviewViewModal';
import RandevuOlusturModal from '../components/RandevuOlusturModal';
import { normalizePreInterviewDurum, arsivDurumlari } from '../components/PreInterviewConstants';
import { notTarihiToDate } from '../components/PreInterviewNotes';

const hizmetTipleri = ['Cam Filmi', 'PPF Kaplama', 'Seramik Kaplama', 'Detay Temizlik', 'Yıkama', 'Göçük Düzeltme', 'Diğer'];

const PreInterviews = () => {
    const [loading, setLoading] = useState(true);
    const [interviews, setInterviews] = useState([]);
    const [activeTab, setActiveTab] = useState('Yeni Görüşme');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const [serviceFilter, setServiceFilter] = useState('');

    // Görüntüleme modu için state
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingInterview, setViewingInterview] = useState(null);

    // Randevu oluşturma modalı için state
    const [isRandevuModalOpen, setIsRandevuModalOpen] = useState(false);
    const [randevuInterviewData, setRandevuInterviewData] = useState(null);

    const navigate = useNavigate();

    const tabs = ['Yeni Görüşme', 'Taslak', 'Arşiv'];

    // orderBy("createdAt") KULLANILMAZ: Firestore, sıralama alanı BULUNMAYAN dokümanı
    // sonuç kümesine hiç koymaz. Canlıda 94 ön görüşmenin 20'sinde `createdAt` yok
    // (v1 kayıtları `olusturmaTarihi` taşıyor) ve 'Yeni'/'Aranacak' durumundaki 8 kaydın
    // TAMAMI bu gruptaydı — sekme normalizasyonu tek başına onları görünür yapmıyordu.
    // Koleksiyon küçük (94 doküman): tamamı okunur, sıralama istemcide yapılır.
    useEffect(() => {
        const q = query(collection(db, "preInterviews"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const kayitlar = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            kayitlar.sort((a, b) => {
                const aTarih = notTarihiToDate(a.createdAt || a.olusturmaTarihi);
                const bTarih = notTarihiToDate(b.createdAt || b.olusturmaTarihi);
                if (aTarih && bTarih) return bTarih - aTarih;
                if (aTarih) return -1;   // tarihsiz kayıtlar en sona
                if (bTarih) return 1;
                return 0;
            });
            setInterviews(kayitlar);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Tanınmayan durumlar (canlıda 'Yeni' ×6, 'Aranacak' ×2) normalize edilir; aksi hâlde
    // hiçbir sekmenin filtresine girmedikleri için kayıtlar uygulamada tamamen görünmez.
    const filteredInterviews = useMemo(() => {
        return interviews.filter(i => {
            const durum = normalizePreInterviewDurum(i.durum);
            let statusMatch = false;
            if (activeTab === 'Arşiv') {
                statusMatch = arsivDurumlari.includes(durum);
            } else if (activeTab === 'Taslak') {
                statusMatch = durum === 'Taslak';
            } else {
                statusMatch = durum === activeTab;
            }

            if (!statusMatch) return false;

            if (serviceFilter) {
                return i.ilgilenilenHizmetler?.includes(serviceFilter);
            }

            return true;
        });
    }, [interviews, activeTab, serviceFilter]);

    const getTabLabel = (tabName) => {
        const durumlari = interviews.map(i => normalizePreInterviewDurum(i.durum));
        let count = 0;
        if (tabName === 'Arşiv') count = durumlari.filter(d => arsivDurumlari.includes(d)).length;
        else count = durumlari.filter(d => d === tabName).length;
        return `${tabName} (${count})`;
    };

    const handleMenuOpen = (e, interview) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setSelectedInterview(interview); };
    const handleMenuClose = () => { setAnchorEl(null); setSelectedInterview(null); };

    const handleStatusUpdate = async (newStatus) => {
        if (!selectedInterview) return;
        await updateDoc(doc(db, "preInterviews", selectedInterview.id), { durum: newStatus });
        handleMenuClose();
    };

    // Randevu Oluşturma Modalını Aç
    const handleCreateAppointment = (interview) => {
        setRandevuInterviewData(interview);
        setIsRandevuModalOpen(true);
    };

    // Randevu oluşturma başarılı olduğunda
    const handleRandevuSuccess = (appointmentId) => {
        setIsRandevuModalOpen(false);
        setRandevuInterviewData(null);
        navigate(`/randevu/${appointmentId}`);
    };

    const handleDeleteInterview = async () => {
        if (!selectedInterview || !window.confirm(`${selectedInterview.adSoyad} adlı ön görüşme kaydını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
            handleMenuClose();
            return;
        }
        try {
            await deleteDoc(doc(db, "preInterviews", selectedInterview.id));
            alert('Ön görüşme kaydı başarıyla silindi.');
        } catch (error) {
            console.error("Ön görüşme kaydı silinirken hata oluştu:", error);
            alert("Bir hata oluştu, kayıt silinemedi.");
        }
        handleMenuClose();
    };

    const handleOpenModal = (interview = null) => {
        if (interview) {
            navigate(`/on-gorusme/duzenle/${interview.id}`);
        } else {
            navigate(`/on-gorusme/yeni`);
        }
        handleMenuClose();
    };

    const handleServiceFilterChange = (event, newFilter) => {
        setServiceFilter(newFilter || '');
    };

    if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;

    return (
        <Box>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} spacing={2}>
                <Grid item xs={12} md={9}>
                    <Stack direction="row" sx={{ overflowX: 'auto', pb: 1 }}>
                        <ToggleButtonGroup
                            value={serviceFilter}
                            exclusive
                            onChange={handleServiceFilterChange}
                            size="small"
                        >
                            <ToggleButton value="" sx={{ whiteSpace: 'nowrap' }}>
                                Tümü
                            </ToggleButton>
                            {hizmetTipleri.map((hizmet) => (
                                <ToggleButton key={hizmet} value={hizmet} sx={{ whiteSpace: 'nowrap' }}>
                                    {hizmet}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Button fullWidth variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => handleOpenModal()}>Yeni Ön Görüşme</Button>
                </Grid>
            </Grid>

            <Paper>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    {tabs.map(tab => <Tab key={tab} label={getTabLabel(tab)} value={tab} />)}
                </Tabs>

                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <Grid container spacing={2}>
                        {filteredInterviews.length > 0 ? (
                            filteredInterviews.map(item => (
                                <Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
                                    <PreInterviewCard
                                        interview={item}
                                        onMenuOpen={handleMenuOpen}
                                        onEdit={() => { setViewingInterview(item); setIsViewModalOpen(true); }}
                                        onCreateWorkOrder={handleCreateAppointment}
                                    />
                                </Grid>
                            ))
                        ) : (
                            <Typography sx={{ p: 4, width: '100%', textAlign: 'center', color: 'text.secondary' }}>
                                Bu kritere uygun ön görüşme kaydı bulunamadı.
                            </Typography>
                        )}
                    </Grid>
                </Box>
            </Paper>

            {/* Görüntüleme Modalı */}
            <PreInterviewViewModal
                isOpen={isViewModalOpen}
                onClose={() => { setIsViewModalOpen(false); setViewingInterview(null); }}
                interview={viewingInterview}
                onEdit={handleOpenModal}
                onCreateWorkOrder={handleCreateAppointment}
            />

            {/* Randevu Oluşturma Modalı */}
            <RandevuOlusturModal
                isOpen={isRandevuModalOpen}
                onClose={() => { setIsRandevuModalOpen(false); setRandevuInterviewData(null); }}
                preInterviewData={randevuInterviewData}
                onSuccess={handleRandevuSuccess}
            />

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={() => handleOpenModal(selectedInterview)}>Düzenle / Detay</MenuItem>
                <Divider />
                <MenuItem onClick={() => handleStatusUpdate('Yeni Görüşme')}>Yeni Olarak İşaretle</MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('Taslak')}>Taslak Olarak İşaretle</MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('İptal Oldu')}>İptal Et</MenuItem>
                <Divider />
                <MenuItem
                    onClick={handleDeleteInterview}
                    disabled={!selectedInterview || !['İptal Oldu', 'Müşteriye Dönüştü'].includes(selectedInterview.durum)}
                    sx={{ color: 'error.main' }}
                >
                    Kaydı Sil (Arşivden)
                </MenuItem>
            </Menu>
        </Box>
    );
};
export default PreInterviews;